import { useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight, BookOpen, FileText, CheckCircle2, Presentation, Headphones, Video, Layers, Network, HelpCircle, type LucideIcon } from "lucide-react";
import { useChapters } from "@/hooks/useCurriculum";
import { formatRelative } from "@/lib/format";
import { cn } from "@/lib/cn";
import type { UserProgress } from "@/lib/api-types";

const STUDY_MODES = ["textbook", "notes", "question-bank", "slides", "podcast", "video", "flashcards", "mind-map", "quiz"] as const;
type StudyMode = (typeof STUDY_MODES)[number];

const MODE_ICON: Record<StudyMode, LucideIcon> = {
  textbook:        BookOpen,
  notes:           FileText,
  "question-bank": CheckCircle2,
  slides:          Presentation,
  podcast:         Headphones,
  video:           Video,
  flashcards:      Layers,
  "mind-map":      Network,
  quiz:            HelpCircle,
};

const MODE_LABEL: Record<StudyMode, string> = {
  textbook:        "Smart Textbook",
  notes:           "Concept Notes",
  "question-bank": "Question Bank",
  slides:          "Slide Deck",
  podcast:         "Podcast",
  video:           "Video Overview",
  flashcards:      "Flashcards",
  "mind-map":      "Mind Map",
  quiz:            "Quiz",
};

export type ResumeCardProps = {
  resume: UserProgress | null;
  /** Optional first name -- triggers the occasional personalised line. */
  firstName?: string;
  /** When true the card animates in immediately (used on dashboard mount). */
  initialEnter?: boolean;
};

/**
 * The "Continue where you left off" card. The single most-used surface on
 * the dashboard -- one tap and you're back in the viewer for the exact
 * mode you last touched. Includes a thin progress bar showing how far
 * you are through the chapter.
 *
 * Falls back to a friendly "pick your first chapter" CTA when the user
 * has no progress yet.
 */
export function ResumeCard({ resume, firstName, initialEnter = true }: ResumeCardProps) {
  // Use the most recent backend-recorded mode if available, else default
  // to textbook (works for chapters with progress where mode_progress is
  // empty -- e.g. someone only opened the chapter).
  const lastMode = useMemo<StudyMode>(() => {
    if (!resume?.mode_progress) return "textbook";
    const modes = Object.keys(resume.mode_progress);
    if (modes.length === 0) return "textbook";
    // Sort by updated_at desc and pick the first one that's a known mode.
    const sorted = modes.sort((a, b) => {
      const ta = new Date(resume.mode_progress![a]?.updated_at ?? 0).getTime();
      const tb = new Date(resume.mode_progress![b]?.updated_at ?? 0).getTime();
      return tb - ta;
    });
    const known = sorted.find((m) => (STUDY_MODES as readonly string[]).includes(m));
    return (known as StudyMode | undefined) ?? "textbook";
  }, [resume]);

  // Resolve chapter name via useChapters(subject_id). Only fires when we have a resume.
  const chapters = useChapters(resume?.subject_id);
  const chapterName = useMemo(() => {
    if (!resume) return null;
    return chapters.data?.find((c) => c.chapter_id === resume.chapter_id)?.chapter_name ?? null;
  }, [resume, chapters.data]);

  if (!resume) return <ResumeEmpty firstName={firstName} initialEnter={initialEnter} />;

  const Icon = MODE_ICON[lastMode];
  const modeLabel = MODE_LABEL[lastMode];
  const href = `/study/${encodeURIComponent(resume.chapter_id)}/${lastMode}`;
  // Backend can return undefined / NaN for chapter_completion_percent when
  // the chapter has no total_modes_available yet (e.g. only chapter_opened
  // recorded, no mode_completed). Coerce defensively -- Math.round(NaN) is
  // NaN, which slips past the min/max guards and renders "NaN%".
  const rawPct = resume.chapter_completion_percent;
  const percent = Number.isFinite(rawPct)
    ? Math.min(100, Math.max(0, Math.round(rawPct)))
    : 0;

  // Always neutral. Names live in the greeting hero -- repeating them
  // on the resume card felt redundant.
  void firstName;
  const headline = "Pick up where you left off.";

  return (
    <motion.div
      initial={initialEnter ? { opacity: 0, y: 8 } : false}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
      className="relative"
    >
      <Link
        to={href}
        className={cn(
          "group block rounded-2xl border border-border bg-surface",
          "p-5 sm:p-6",
          "transition-[border-color,box-shadow,transform] duration-200 ease-out",
          "hover:border-border-strong hover:shadow-[0_6px_20px_rgb(15_23_42_/_0.07)] hover:-translate-y-px",
          "active:translate-y-0",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        )}
      >
        <div className="flex items-center justify-between gap-3">
          <span className="text-[10px] uppercase tracking-[0.16em] text-text-subtle">
            {headline}
          </span>
          <ChevronRight className="h-4 w-4 text-text-subtle group-hover:translate-x-0.5 group-hover:text-text transition-all" />
        </div>

        <div className="mt-3 flex items-start gap-3">
          <span className={cn(
            "grid place-items-center h-10 w-10 rounded-lg bg-brand-soft text-brand flex-shrink-0",
            "transition-transform duration-200 group-hover:scale-[1.04]",
          )}>
            <Icon className="h-5 w-5" />
          </span>
          <div className="flex-1 min-w-0">
            <h3 className="font-display text-base sm:text-lg font-semibold tracking-tight truncate">
              {chapterName ?? "Loading chapter…"}
            </h3>
            <p className="mt-0.5 text-sm text-text-muted">
              {modeLabel}
              <span className="text-text-subtle"> · </span>
              <span>last opened {formatRelative(resume.last_accessed_at)}</span>
            </p>
          </div>
        </div>

        <div className="mt-4">
          <div className="flex items-end justify-between mb-1.5">
            <span className="text-[11px] uppercase tracking-[0.14em] text-text-subtle">Chapter progress</span>
            <span className="text-xs tabular-nums text-text-muted">
              <span className="text-text font-medium">{percent}%</span>
            </span>
          </div>
          <div className="h-1.5 rounded-full bg-surface-2 overflow-hidden">
            <motion.div
              className="h-full bg-brand"
              initial={{ width: 0 }}
              animate={{ width: `${percent}%` }}
              transition={{ type: "spring", stiffness: 200, damping: 28, delay: 0.15 }}
            />
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

function ResumeEmpty({ firstName, initialEnter }: { firstName?: string; initialEnter: boolean }) {
  const greet = firstName && firstName !== "you" ? `, ${firstName}` : "";
  return (
    <motion.div
      initial={initialEnter ? { opacity: 0, y: 8 } : false}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
      className="rounded-2xl border border-border bg-surface p-5 sm:p-6"
    >
      <span className="text-[10px] uppercase tracking-[0.16em] text-text-subtle">
        Get started
      </span>
      <h3 className="mt-2 font-display text-lg font-semibold tracking-tight">
        Pick your first chapter{greet}.
      </h3>
      <p className="mt-1 text-sm text-text-muted">
        Free chapters are open on every subject. Start anywhere — we'll keep your spot.
      </p>
      <Link
        to="/subjects"
        className="mt-4 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-brand text-white text-sm font-medium hover:opacity-95 transition-opacity"
      >
        Browse subjects
        <ChevronRight className="h-4 w-4" />
      </Link>
    </motion.div>
  );
}

